namespace Client

module FetchRequest = 
    open Elmish
    open Fable.Core
    open Fetch
    open Thoth.Json

    module private CustomEncoders = 
        ///Override the default behavior of Thoth which has a bug with Daylight savings
        let datetime (value : System.DateTime) : JsonValue =
            value.ToString("yyyy-MM-ddTHH:mm:ss.fff") |> Encode.string

    module private CustomDecoders = 
        open Thoth.Json
        open System.Globalization

        let inline private isString (o: JsonValue) : bool = o :? string
        let inline private asString (o: JsonValue): string = unbox o

        ///Override the default behavior of Thoth which is to fail if a string is null
        let string: Decoder<string> =
            fun path value ->
                if isString value then
                    Ok(asString value)
                else Ok null
        
        ///Override the default behavior of Thoth which is to convert dates to UTC
        let datetime: Decoder<System.DateTime> =
            fun path value ->
                if isString value then
                    match System.DateTime.TryParse (asString value) with
                    | true, x -> x.ToUniversalTime().ToLocalTime() |> Ok
                    | _ -> (path, BadPrimitive("a datetime", value)) |> Error
                else
                    (path, BadPrimitive("a datetime", value)) |> Error

    let extraCoders = 
        Extra.empty
        |> Extra.withCustom Encode.string CustomDecoders.string
        |> Extra.withCustom CustomEncoders.datetime CustomDecoders.datetime
        |> Extra.withDecimal

    let inline getPromise<'Response> url overwriteProps: JS.Promise<'Response> =
        promise {
            let props = 
                overwriteProps
                |> Option.defaultValue
                    [ 
                        Fetch.requestHeaders [
                            HttpRequestHeaders.ContentType "application/json"
                        ]
                    ]

            return! Thoth.Fetch.Fetch.get(url, properties=props, extra=extraCoders)
        }

    let inline get url (onSuccess: 'Response -> 'Msg) (onFailure: exn -> 'Msg) = 
        Cmd.OfPromise.either (fun _ -> getPromise<'Response> url None) () onSuccess onFailure

    
    let inline getWithToken url token onSuccess onFailure =
        let promise = 
            [
                Fetch.requestHeaders [
                    HttpRequestHeaders.ContentType "application/json"
                    HttpRequestHeaders.Authorization ("Bearer " + token)
                ]
            ] 
            |> Some 
            |> getPromise url
        Cmd.OfPromise.either (fun _ -> promise) () onSuccess onFailure


    let inline deletePromise<'DTO, 'Response> url overwriteProps (dto: 'DTO): JS.Promise<'Response> =
        promise {
            let props = 
                overwriteProps
                |> Option.defaultValue
                    [ 
                        Fetch.requestHeaders [
                            HttpRequestHeaders.ContentType "application/json"
                        ]
                    ]

            return! Thoth.Fetch.Fetch.delete(url, dto, properties=props, extra=extraCoders)
        }

    let inline deleteWithToken url dto token (onSuccess: 'Response -> 'Msg) (onFailure: exn -> 'Msg) =
        let promise = 
            [
                Fetch.requestHeaders [
                    HttpRequestHeaders.ContentType "application/json"
                    HttpRequestHeaders.Authorization ("Bearer " + token)
                ]
            ] 
            |> Some 
            |> deletePromise url
        Cmd.OfPromise.either promise dto onSuccess onFailure


    let inline postPromise<'DTO, 'Response> url overwriteProps (dto: 'DTO): JS.Promise<'Response> =
        promise {
            let props = 
                overwriteProps 
                |> Option.defaultValue
                    [ 
                        yield Fetch.requestHeaders [
                            ContentType "application/json" 
                        ]
                    ]

            return! Thoth.Fetch.Fetch.post(url, dto, properties=props, extra=extraCoders)
        }

    let inline post url dto (onSuccess: 'Response -> 'Msg) (onFailure: exn -> 'Msg) = 
        Cmd.OfPromise.either (postPromise url None) dto onSuccess onFailure

    let inline postNoDTOPromise<'Response> url overwriteProps : JS.Promise<'Response> =
        promise {
            let props = 
                overwriteProps 
                |> Option.defaultValue
                    [ 
                        yield Fetch.requestHeaders [
                            ContentType "application/json" 
                        ]
                    ]

            return! Thoth.Fetch.Fetch.post(url, data=None, properties=props, extra=extraCoders)
        }

    let inline postNoDTO url (onSuccess: 'Response -> 'Msg) (onFailure: exn -> 'Msg) = 
        Cmd.OfPromise.either (fun _ -> postNoDTOPromise url None) () onSuccess onFailure

    let inline postNoDTOWithCredentials url (onSuccess: 'Response -> 'Msg) (onFailure: exn -> 'Msg) = 
        Cmd.OfPromise.either 
            (fun _ -> postNoDTOPromise url (Some([
                Fetch.requestHeaders [
                    ContentType "application/json"  
                ]
                Credentials RequestCredentials.Include
            ])))
            () 
            onSuccess 
            onFailure

    let inline putPromise<'DTO, 'Response> url overwriteProps (dto: 'DTO): JS.Promise<'Response> =
        promise {
            let props = 
                overwriteProps 
                |> Option.defaultValue
                    [ 
                        yield Fetch.requestHeaders [
                            ContentType "application/json" 
                        ]
                    ]

            return! Thoth.Fetch.Fetch.put(url, dto, properties=props, extra=extraCoders)
        }

    let inline put url dto (onSuccess: 'Response -> 'Msg) (onFailure: exn -> 'Msg) = 
        Cmd.OfPromise.either (putPromise url None) dto onSuccess onFailure

    let inline postWithToken url dto token onSuccess onFailure =
        let promise = 
            [
                Fetch.requestHeaders [
                    HttpRequestHeaders.ContentType "application/json"
                    HttpRequestHeaders.Authorization ("Bearer " + token)
                ]
            ] 
            |> Some 
            |> postPromise url
        Cmd.OfPromise.either promise dto onSuccess onFailure

module BrowserLocalStorage  =
    open Thoth.Json
    open Browser.WebStorage

    let inline load<'T> key: Result<'T,string> =
        let o = localStorage.getItem(key)
        if isNull o
        then "No item found in local storage with key " + key |> Error
        else Decode.Auto.fromString o

    let delete key =
        localStorage.removeItem(key)

    let inline save key (data: 'T) =
        localStorage.setItem(key, Encode.Auto.toString(0, data))


module Utils = 

    open Fetch
    open Fable.Core

    let appendToken token =
        [ 
            Fetch.requestHeaders [
                HttpRequestHeaders.Authorization ("Bearer " + token)
            ]
        ]

    let emptyString = System.String.IsNullOrWhiteSpace
    let (|NOTEMPTY|_|) str =
        if emptyString str then None else Some(str)

    let be num = if num = 1 then "is" else "are"
    let plural last num = 
        match last,num with
        | _,1 -> last
        | "y",_ -> "ies"
        | _ -> last + "s"

    let optString = function None -> "" | Some x -> x.ToString()

    let formatDate d f = Date.Format.localFormat Date.Local.englishUK d f